import React, { useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const PooledJobCardPrint = ({ pooledJob, autoPrint = false, onPrintComplete }) => {
  const hasTriggeredPrint = useRef(false);

  useEffect(() => {
    if (autoPrint && pooledJob && !hasTriggeredPrint.current) {
      hasTriggeredPrint.current = true;
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [autoPrint, pooledJob]);

  const handlePrint = () => {
    const workers = pooledJob?.workers || [];
    const materials = pooledJob?.combined_materials || [];
    const lineItems = pooledJob?.line_items || [];
    
    // Parse and format the issued_at time in IST timezone
    let issuedDate;
    try {
      if (pooledJob?.issued_at) {
        issuedDate = formatInTimeZone(parseISO(pooledJob.issued_at), 'Asia/Kolkata', 'dd/MM/yyyy HH:mm');
      } else {
        issuedDate = formatInTimeZone(new Date(), 'Asia/Kolkata', 'dd/MM/yyyy HH:mm');
      }
    } catch (e) {
      // Fallback if parsing fails
      issuedDate = pooledJob?.issued_at 
        ? format(new Date(pooledJob.issued_at), 'dd/MM/yyyy HH:mm') 
        : format(new Date(), 'dd/MM/yyyy HH:mm');
    }
    
    // Line items table rows
    const lineItemRows = lineItems.map((li, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td>${li.work_order_number}</td>
        <td>${li.item_name}</td>
        <td style="text-align: right;">${li.quantity_to_produce}</td>
        <td style="text-align: center;">${li.unit}</td>
        <td style="width: 80px;"></td>
        <td style="width: 80px;"></td>
      </tr>
    `).join('');

    // Material rows
    const materialRows = materials.map((mat, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td>${mat.item_name}</td>
        <td style="text-align: center;">${mat.item_code || '-'}</td>
        <td style="text-align: right;">${mat.total_quantity.toFixed(2)}</td>
        <td style="text-align: center;">${mat.unit}</td>
        <td style="width: 100px;"></td>
      </tr>
    `).join('');

    // Add empty rows if less than 8 materials
    const emptyMaterialRows = materials.length < 8 
      ? [...Array(8 - materials.length)].map((_, idx) => `
        <tr>
          <td style="text-align: center;">${materials.length + idx + 1}</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      `).join('')
      : '';

    // Worker rows (fallback for renamed field: employee_name || worker_name)
    const workerRows = workers.length > 0 
      ? workers.map((w, idx) => `
        <tr>
          <td style="text-align: center;">${idx + 1}</td>
          <td>${w.employee_name || w.worker_name || ''}</td>
          <td>${w.role?.replace('_', ' ') || ''}</td>
          <td></td>
        </tr>
      `).join('')
      : '';
    
    const emptyWorkerRows = [...Array(Math.max(5 - workers.length, 0))].map((_, idx) => `
      <tr>
        <td style="text-align: center;">${workers.length + idx + 1}</td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    `).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Pooled Job Card - ${pooledJob?.pooled_job_number || ''}</title>
          <style>
            @page { size: A4; margin: 8mm; }
            @media print { .page-break { page-break-before: always; } }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.3; color: #000; }
            .page { width: 100%; max-width: 194mm; min-height: 277mm; margin: 0 auto; padding: 3mm; }
            .header { text-align: center; border: 2px solid #000; padding: 8px; margin-bottom: 8px; }
            .header h1 { font-size: 16pt; font-weight: bold; margin: 0; }
            .header h2 { font-size: 11pt; font-weight: normal; margin: 3px 0 0 0; color: #555; }
            .section-title { font-weight: bold; border: 1px solid #000; padding: 5px 8px; font-size: 10pt; background: #f5f5f5; margin-bottom: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
            th, td { border: 1px solid #000; padding: 4px 6px; text-align: left; font-size: 9pt; vertical-align: top; }
            th { background: #f5f5f5; font-weight: bold; }
            .info-table td { height: 22px; }
            .info-label { font-weight: bold; background: #f5f5f5; width: 22%; }
            .info-value { width: 28%; }
            .checkbox { display: inline-block; width: 12px; height: 12px; border: 1px solid #000; margin-right: 3px; vertical-align: middle; }
            .signature-row td { height: 45px; vertical-align: bottom; padding-bottom: 8px; }
            .remarks-box { border: 1px solid #000; min-height: 50px; padding: 5px; margin-bottom: 8px; }
            .page-title { text-align: center; font-size: 9pt; color: #666; margin-bottom: 5px; }
          </style>
        </head>
        <body>
          <!-- PAGE 1: FRONT -->
          <div class="page">
            <div class="header">
              <h1>POOLED JOB CARD</h1>
              <h2>(Multiple Work Orders Combined)</h2>
            </div>
            
            <!-- Job Information -->
            <table class="info-table">
              <tr>
                <td class="info-label">Pooled Job No.</td>
                <td class="info-value" style="font-weight: bold; font-size: 12pt;">${pooledJob.pooled_job_number}</td>
                <td class="info-label">Issue Date</td>
                <td class="info-value">${issuedDate}</td>
              </tr>
              <tr>
                <td class="info-label">Process Name</td>
                <td class="info-value" colspan="3" style="font-weight: bold;">${pooledJob.process_name}</td>
              </tr>
              <tr>
                <td class="info-label">Drawing No.</td>
                <td class="info-value">${pooledJob.drawing_number || ''}</td>
                <td class="info-label">Machine</td>
                <td class="info-value">${pooledJob.machine || ''}</td>
              </tr>
              <tr>
                <td class="info-label">Total Quantity</td>
                <td class="info-value" style="font-weight: bold; font-size: 12pt;">${pooledJob.total_quantity}</td>
                <td class="info-label">No. of WOs</td>
                <td class="info-value">${lineItems.length}</td>
              </tr>
            </table>

            <!-- Work Orders / Line Items -->
            <div class="section-title">WORK ORDERS INCLUDED</div>
            <table>
              <thead>
                <tr>
                  <th style="width: 6%; text-align: center;">S.No</th>
                  <th style="width: 18%;">WO Number</th>
                  <th style="width: 30%;">Item</th>
                  <th style="width: 12%; text-align: right;">Qty</th>
                  <th style="width: 8%; text-align: center;">Unit</th>
                  <th style="width: 13%; text-align: center;">Completed</th>
                  <th style="width: 13%; text-align: center;">Defects</th>
                </tr>
              </thead>
              <tbody>
                ${lineItemRows}
              </tbody>
            </table>

            <!-- Raw Materials Section -->
            <div class="section-title">RAW MATERIALS ISSUED (RMT) - Combined</div>
            <table>
              <thead>
                <tr>
                  <th style="width: 8%; text-align: center;">S.No</th>
                  <th style="width: 35%;">Material Name</th>
                  <th style="width: 15%; text-align: center;">Code</th>
                  <th style="width: 15%; text-align: right;">Total Qty</th>
                  <th style="width: 10%; text-align: center;">Unit</th>
                  <th style="width: 17%; text-align: center;">Issued Qty</th>
                </tr>
              </thead>
              <tbody>
                ${materialRows}
                ${emptyMaterialRows}
              </tbody>
            </table>
            
            <table style="margin-top: 5px;">
              <tr>
                <td style="width: 50%; border: none; padding-left: 0;">
                  <strong>Issued By:</strong> ${pooledJob.issued_by_name || '__________________'} 
                  <strong>Sign:</strong> __________________
                </td>
                <td style="width: 50%; border: none; text-align: right; padding-right: 0;">
                  <strong>Date:</strong> __________________
                </td>
              </tr>
            </table>
          </div>

          <!-- PAGE 2: BACK -->
          <div class="page page-break">
            <div class="page-title">Pooled Job Card: ${pooledJob.pooled_job_number} (Back)</div>
            
            <!-- Workers Section -->
            <div class="section-title">WORKERS ASSIGNED</div>
            <table>
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">S.No</th>
                  <th style="width: 45%;">Worker Name</th>
                  <th style="width: 25%;">Role</th>
                  <th style="width: 20%;">Signature</th>
                </tr>
              </thead>
              <tbody>
                ${workerRows}
                ${emptyWorkerRows}
              </tbody>
            </table>

            <!-- First Piece Approval -->
            <div class="section-title">FIRST PIECE APPROVAL</div>
            <table>
              <tr>
                <td class="info-label" style="width: 25%;">First Piece Approved</td>
                <td style="width: 25%;">
                  <span class="checkbox"></span> Yes &nbsp;&nbsp;&nbsp;
                  <span class="checkbox"></span> No
                </td>
                <td class="info-label" style="width: 25%;">Date & Time</td>
                <td style="width: 25%;"></td>
              </tr>
              <tr>
                <td class="info-label">Approved By (Name)</td>
                <td></td>
                <td class="info-label">Signature</td>
                <td></td>
              </tr>
            </table>

            <!-- Production Details -->
            <div class="section-title">PRODUCTION DETAILS</div>
            <table>
              <tr>
                <td class="info-label" style="width: 25%;">Actual Start Date/Time</td>
                <td style="width: 25%;"></td>
                <td class="info-label" style="width: 25%;">Actual End Date/Time</td>
                <td style="width: 25%;"></td>
              </tr>
              <tr>
                <td class="info-label">Total Hours Worked</td>
                <td colspan="3"></td>
              </tr>
            </table>

            <!-- Quality Check -->
            <div class="section-title">QUALITY CHECK</div>
            <table>
              <tr>
                <td class="info-label" style="width: 25%;">QC Result</td>
                <td style="width: 25%;">
                  <span class="checkbox"></span> Pass &nbsp;&nbsp;&nbsp;
                  <span class="checkbox"></span> Fail
                </td>
                <td class="info-label" style="width: 25%;">Inspected By</td>
                <td style="width: 25%;"></td>
              </tr>
              <tr>
                <td class="info-label">Date & Time</td>
                <td></td>
                <td class="info-label">Signature</td>
                <td></td>
              </tr>
              <tr>
                <td class="info-label">Rejection Reason (if any)</td>
                <td colspan="3"></td>
              </tr>
            </table>

            <!-- Remarks -->
            <div class="section-title">REMARKS / SPECIAL INSTRUCTIONS</div>
            <div class="remarks-box">
              ${pooledJob.remarks || ''}
            </div>

            <!-- Sign-off Section -->
            <div class="section-title">SIGN-OFF</div>
            <table>
              <tr>
                <th style="width: 50%;">Production Supervisor</th>
                <th style="width: 50%;">Store Keeper</th>
              </tr>
              <tr class="signature-row">
                <td>
                  Name: ____________________<br/>
                  Sign: ____________________ Date: ____________
                </td>
                <td>
                  Name: ____________________<br/>
                  Sign: ____________________ Date: ____________
                </td>
              </tr>
            </table>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      if (onPrintComplete) {
        onPrintComplete();
      }
    }, 300);
  };

  return null;
};

export default PooledJobCardPrint;
